import { Controller } from '@hotwired/stimulus';

/** One PWYW field: $0 → free unlock (Turbo), paid amount → Stripe checkout. */
export default class extends Controller {
  static targets = ['amount', 'error'];

  static values = {
    freeUrl: String,
    checkoutUrl: String,
    resourceGid: String,
    minPaidCents: { type: Number, default: 100 },
    maxCents: Number,
  };

  submit(event) {
    event.preventDefault();

    const dollars = this.parseDollars(this.amountTarget.value);
    const cents = dollars * 100;

    this.clearError();

    if (dollars < 0 || cents > this.maxCentsValue) {
      this.showError(`Enter a whole dollar amount from $0 to $${this.maxDollars()}.`);
      return;
    }

    if (dollars === 0) {
      this.unlockFree();
      return;
    }

    if (cents < this.minPaidCentsValue) {
      this.showError('Enter $0 to take it for free, or at least $1 to pay.');
      return;
    }

    this.startCheckout(dollars);
  }

  parseDollars(raw) {
    if (raw === '' || raw == null) return 0;
    const value = Number.parseInt(String(raw).trim(), 10);
    return Number.isFinite(value) && value >= 0 ? value : 0;
  }

  maxDollars() {
    return Math.floor(this.maxCentsValue / 100);
  }

  formatDollars(amount) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  }

  unlockFree() {
    this.beginUnlocking();

    const body = new FormData();
    body.append('authenticity_token', this.csrfToken);
    body.append('resourceable_gid', this.resourceGidValue);

    fetch(this.freeUrlValue, {
      method: 'POST',
      headers: {
        Accept: 'text/vnd.turbo-stream.html',
        'X-CSRF-Token': this.csrfToken,
      },
      body,
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) throw new Error('unlock_failed');
        return response.text();
      })
      .then((html) => {
        const unlockController = this.memberUnlockController;
        if (unlockController?.renderStreamAfterMinDuration) {
          unlockController.renderStreamAfterMinDuration(html);
        } else {
          window.Turbo?.renderStreamMessage(html);
        }
      })
      .catch(() => {
        this.endUnlocking();
        this.showError('Could not unlock. Try again.');
      });
  }

  startCheckout(dollars) {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = this.checkoutUrlValue;
    form.hidden = true;

    const token = document.createElement('input');
    token.type = 'hidden';
    token.name = 'authenticity_token';
    token.value = this.csrfToken;
    form.appendChild(token);

    const amount = document.createElement('input');
    amount.type = 'hidden';
    amount.name = 'amount_dollars';
    amount.value = String(dollars);
    form.appendChild(amount);

    document.body.appendChild(form);
    form.submit();
  }

  beginUnlocking() {
    this.island?.classList.add('tool-member-island--unlocking');
    this.island?.dispatchEvent(new CustomEvent('tool-member-unlock:start', { bubbles: true }));
    this.amountTarget.disabled = true;
    this.element.querySelector('[type="submit"]')?.setAttribute('disabled', 'disabled');
  }

  endUnlocking() {
    this.island?.classList.remove('tool-member-island--unlocking');
    this.amountTarget.disabled = false;
    this.element.querySelector('[type="submit"]')?.removeAttribute('disabled');
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.hidden = false;
    }
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = '';
      this.errorTarget.hidden = true;
    }
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
  }

  get island() {
    return this.element.closest('.tool-member-island');
  }

  get memberUnlockController() {
    if (!this.island) return null;
    return this.application.getControllerForElementAndIdentifier(this.island, 'tool-member-unlock');
  }
}
